import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Security,
  Schedule,
  VpnKey,
  Extension,
  Lock,
  LockOpen,
  CalendarToday,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useAuthStore } from '../../store/authStore';
import { useTranslation } from '../../store/languageStore';
import authService from '../../shared/services/authService';
import codelistService from '../../services/codelistService';
import ModuleAuthModal from '../../shared/components/ModuleAuthModal';

/**
 * User Modules & Integrations section.
 *
 * Self-contained: owns module list / auth status, BEO Sanctions auth, and codelist
 * refresh state. Extracted from ProfilePage so it can live on the Settings page.
 */
const ModulesSection: React.FC = () => {
  const { user } = useAuthStore();
  const { tString } = useTranslation();

  // BEO Sanctions Authentication State
  const [showSanctionsAuth, setShowSanctionsAuth] = useState(false);
  const [sanctionsModuleId, setSanctionsModuleId] = useState<string | null>(null);
  const [sanctionsAuthStatus, setSanctionsAuthStatus] = useState<any>(null);
  const [sanctionsAuthLoading, setSanctionsAuthLoading] = useState(false);

  // User Modules State
  const [userModules, setUserModules] = useState<any[]>([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState<{ id: string; name: string } | null>(null);
  const [showModuleAuth, setShowModuleAuth] = useState(false);
  const [moduleAuthStatuses, setModuleAuthStatuses] = useState<{ [key: string]: any }>({});
  const [moduleUserDetails, setModuleUserDetails] = useState<{ [key: string]: any }>({});

  // Codelist Refresh State
  const [codelistRefreshing, setCodelistRefreshing] = useState(false);
  const [codelistLastRefreshed, setCodelistLastRefreshed] = useState<string | null>(null);

  // Load BEO Sanctions module and auth status
  useEffect(() => {
    const loadSanctionsModule = async () => {
      try {
        setSanctionsAuthLoading(true);

        // Fetch all modules and find BEO Sanctions
        const modules = await authService.getModules();
        const sanctionsModule = modules.find((m: any) => m.name === 'BEO Sanctions');

        if (sanctionsModule) {
          setSanctionsModuleId(sanctionsModule.id);

          // Check authentication status
          try {
            const status = await authService.checkModuleAuthStatus(sanctionsModule.id);
            setSanctionsAuthStatus({
              authenticated: status.authenticated || false,
              last_updated: status.last_updated || status.updated_at,
              expires_at: status.expires_at,
            });
          } catch (error) {
            console.error('Failed to check sanctions auth status:', error);
            setSanctionsAuthStatus({ authenticated: false });
          }
        } else {
          console.warn('BEO Sanctions module not found');
        }
      } catch (error) {
        console.error('Failed to load sanctions module:', error);
      } finally {
        setSanctionsAuthLoading(false);
      }
    };

    if (user) {
      loadSanctionsModule();
    }
  }, [user]);

  // Load codelist last-refreshed timestamp
  useEffect(() => {
    authService.getCodelistLastRefreshed().then((data) => {
      if (data?.lastRefreshed) setCodelistLastRefreshed(data.lastRefreshed);
    }).catch(() => {});
  }, []);

  const handleCodelistRefresh = async () => {
    setCodelistRefreshing(true);
    try {
      // 1. Tell backend to fetch fresh data from Atlas API into DB
      const result = await authService.refreshCodelists();
      if (result.status === 'success') {
        // 2. Clear frontend localStorage cache so next fetch gets fresh DB data
        await codelistService.forceRefresh();
        // 3. Notify any mounted form components to re-load codelists
        window.dispatchEvent(new Event('codelist:refreshed'));
        toast.success(`Codelists refreshed — ${result.typesRefreshed} types, ${result.recordsUpserted} records updated. Form dropdowns will reflect the latest data.`);
        setCodelistLastRefreshed(result.refreshedAt);
      } else {
        toast.info(result.message || 'No new data from Atlas API');
      }
    } catch {
      toast.error('Refresh failed. Please try again or contact support.');
    } finally {
      setCodelistRefreshing(false);
    }
  };

  // Load user modules and their authentication status
  useEffect(() => {
    const loadUserModules = async () => {
      try {
        setModulesLoading(true);

        // Fetch all modules
        const modules = await authService.getModules();
        setUserModules(modules);

        // Check authentication status and user details for each module
        const statusMap: { [key: string]: any } = {};
        const detailsMap: { [key: string]: any } = {};

        for (const module of modules) {
          try {
            // Fetch authentication status
            const status = await authService.checkModuleAuthStatus(module.id);
            statusMap[module.id] = {
              authenticated: status.authenticated || false,
              last_updated: status.last_updated || status.updated_at,
              expires_at: status.expires_at,
            };

            // Fetch user details if authenticated
            if (status.authenticated) {
              const userDetails = await authService.getModuleUserDetails(module.id);
              if (userDetails && userDetails.found) {
                detailsMap[module.id] = userDetails.user_details;
              }
            }
          } catch (error) {
            console.error(`Failed to check auth status for ${module.name}:`, error);
            statusMap[module.id] = { authenticated: false };
          }
        }

        setModuleAuthStatuses(statusMap);
        setModuleUserDetails(detailsMap);
      } catch (error) {
        console.error('Failed to load user modules:', error);
      } finally {
        setModulesLoading(false);
      }
    };

    if (user) {
      loadUserModules();
    }
  }, [user]);

  // Handle module authentication
  const handleModuleAuthenticate = (moduleId: string, moduleName: string) => {
    setSelectedModule({ id: moduleId, name: moduleName });
    setShowModuleAuth(true);
  };

  // Handle module authentication modal close
  const handleModuleAuthClose = async (authenticated?: boolean) => {
    setShowModuleAuth(false);

    if (authenticated && selectedModule) {
      toast.success(`Successfully authenticated with ${selectedModule.name}!`);

      // Reload auth status and user details for this module
      try {
        const status = await authService.checkModuleAuthStatus(selectedModule.id);
        setModuleAuthStatuses(prev => ({
          ...prev,
          [selectedModule.id]: {
            authenticated: status.authenticated || true,
            last_updated: status.last_updated || new Date().toISOString(),
            expires_at: status.expires_at,
          },
        }));

        // Fetch user details after successful authentication
        const userDetails = await authService.getModuleUserDetails(selectedModule.id);
        if (userDetails && userDetails.found) {
          setModuleUserDetails(prev => ({
            ...prev,
            [selectedModule.id]: userDetails.user_details,
          }));
        }
      } catch (error) {
        console.error('Failed to reload module auth status:', error);
      }
    }

    setSelectedModule(null);
  };

  // Handle sanctions authentication modal close
  const handleSanctionsAuthClose = async (authenticated?: boolean) => {
    setShowSanctionsAuth(false);

    // Only reload status and show success if authentication succeeded
    if (authenticated) {
      toast.success('BEO Sanctions authentication successful!');

      // Reload auth status
      if (sanctionsModuleId) {
        try {
          const status = await authService.checkModuleAuthStatus(sanctionsModuleId);
          setSanctionsAuthStatus({
            authenticated: status.authenticated || true,
            last_updated: status.last_updated || new Date().toISOString(),
            expires_at: status.expires_at,
          });
        } catch (error) {
          console.error('Failed to reload sanctions auth status:', error);
        }
      }
    }
  };

  // Handle sanctions authentication reset
  const handleSanctionsReset = async () => {
    if (!sanctionsModuleId) return;
    try {
      setSanctionsAuthLoading(true);
      await authService.revokeModuleAuth(sanctionsModuleId);
      setSanctionsAuthStatus({ authenticated: false });
      toast.success('BEO Sanctions authentication has been reset.');
    } catch (error) {
      console.error('Failed to reset sanctions auth:', error);
      toast.error('Failed to reset BEO Sanctions authentication.');
    } finally {
      setSanctionsAuthLoading(false);
    }
  };

  return (
    <>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Extension color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {tString('userProfile.modules.title')}
            </Typography>
          </Box>
          {modulesLoading && <CircularProgress size={20} />}
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          {tString('userProfile.modules.subtitle')}
        </Alert>

        {modulesLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {userModules.map((module) => {
              const authStatus = moduleAuthStatuses[module.id];
              const isAuthenticated = authStatus?.authenticated || false;
              const userDetails = moduleUserDetails[module.id];

              return (
                <Grid item xs={12} md={6} key={module.id}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Extension color={isAuthenticated ? 'success' : 'disabled'} />
                          <Typography variant="h6" fontWeight={600}>
                            {module.name}
                          </Typography>
                        </Box>
                        <Chip
                          label={isAuthenticated ? tString('userProfile.modules.authenticated') : tString('userProfile.modules.notAuthenticated')}
                          color={isAuthenticated ? 'success' : 'default'}
                          size="small"
                          icon={isAuthenticated ? <LockOpen /> : <Lock />}
                        />
                      </Box>

                      {module.description && (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          {module.description}
                        </Typography>
                      )}

                      {isAuthenticated && authStatus && (
                        <Box sx={{ mb: 2 }}>
                          <List dense>
                            <ListItem sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CalendarToday fontSize="small" color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Last Authenticated"
                                secondary={authStatus.last_updated ?
                                  new Date(authStatus.last_updated).toLocaleString() :
                                  'Unknown'}
                                primaryTypographyProps={{ variant: 'caption' }}
                                secondaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                            {authStatus.expires_at && (
                              <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Schedule fontSize="small" color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                  primary="Expires"
                                  secondary={new Date(authStatus.expires_at).toLocaleDateString()}
                                  primaryTypographyProps={{ variant: 'caption' }}
                                  secondaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            )}
                          </List>

                          {/* Display User Details if available */}
                          {userDetails && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="caption" color="primary" fontWeight={600} gutterBottom display="block">
                                Module Details
                              </Typography>
                              {userDetails.atlas_kundennummer && (
                                <Typography variant="caption" display="block">
                                  <strong>Customer Number:</strong> {userDetails.atlas_kundennummer}
                                </Typography>
                              )}
                              {userDetails.atlas_mandant_id && (
                                <Typography variant="caption" display="block">
                                  <strong>Mandant ID:</strong> {userDetails.atlas_mandant_id}
                                </Typography>
                              )}
                              {userDetails.atlas_user_id && (
                                <Typography variant="caption" display="block">
                                  <strong>User ID:</strong> {userDetails.atlas_user_id}
                                </Typography>
                              )}
                              {userDetails.email && (
                                <Typography variant="caption" display="block">
                                  <strong>Email:</strong> {userDetails.email}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      )}

                      <Button
                        variant={isAuthenticated ? 'outlined' : 'contained'}
                        color="primary"
                        size="small"
                        fullWidth
                        startIcon={<VpnKey />}
                        onClick={() => handleModuleAuthenticate(module.id, module.name)}
                      >
                        {tString('userProfile.modules.authenticate')}
                      </Button>
                      <Tooltip title={isAuthenticated ? 'Refresh codelist dropdown data from Atlas' : 'Authenticate first to refresh codelists'}>
                        <span>
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            fullWidth
                            startIcon={codelistRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                            onClick={handleCodelistRefresh}
                            disabled={codelistRefreshing || !isAuthenticated}
                            sx={{ mt: 1 }}
                          >
                            {codelistRefreshing ? 'Refreshing Codelists…' : 'Refresh Codelists'}
                          </Button>
                        </span>
                      </Tooltip>
                      {codelistLastRefreshed && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                          Codelists last refreshed: {new Date(codelistLastRefreshed).toLocaleString()}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}

            {/* BEO Sanctions Card */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Security color={sanctionsAuthStatus?.authenticated ? 'success' : 'disabled'} />
                      <Typography variant="h6" fontWeight={600}>
                        BEO Sanctions
                      </Typography>
                    </Box>
                    <Chip
                      label={sanctionsAuthStatus?.authenticated ? tString('userProfile.modules.authenticated') : tString('userProfile.modules.notAuthenticated')}
                      color={sanctionsAuthStatus?.authenticated ? 'success' : 'default'}
                      size="small"
                      icon={sanctionsAuthStatus?.authenticated ? <LockOpen /> : <Lock />}
                    />
                  </Box>

                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Real-time sanctions screening via BEO Sanctions API
                  </Typography>

                  {sanctionsAuthStatus?.authenticated && (
                    <Box sx={{ mb: 2 }}>
                      <List dense>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CalendarToday fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Last Authenticated"
                            secondary={sanctionsAuthStatus.last_updated ?
                              new Date(sanctionsAuthStatus.last_updated).toLocaleString() :
                              'Unknown'}
                            primaryTypographyProps={{ variant: 'caption' }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        {sanctionsAuthStatus.expires_at && (
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Schedule fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Expires"
                              secondary={new Date(sanctionsAuthStatus.expires_at).toLocaleDateString()}
                              primaryTypographyProps={{ variant: 'caption' }}
                              secondaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )}

                  {sanctionsAuthLoading ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant={sanctionsAuthStatus?.authenticated ? 'outlined' : 'contained'}
                        color="primary"
                        size="small"
                        fullWidth
                        startIcon={<VpnKey />}
                        onClick={() => setShowSanctionsAuth(true)}
                      >
                        {sanctionsAuthStatus?.authenticated ? 'Re-authenticate' : tString('userProfile.modules.authenticate')}
                      </Button>
                      {sanctionsAuthStatus?.authenticated && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<LockOpen />}
                          onClick={handleSanctionsReset}
                        >
                          Reset
                        </Button>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Module Auth Modal for User Modules */}
      {showModuleAuth && selectedModule && (
        <ModuleAuthModal
          open={showModuleAuth}
          onClose={handleModuleAuthClose}
          moduleId={selectedModule.id}
          moduleName={selectedModule.name}
        />
      )}

      {/* Module Auth Modal for BEO Sanctions */}
      {showSanctionsAuth && sanctionsModuleId && (
        <ModuleAuthModal
          open={showSanctionsAuth}
          onClose={handleSanctionsAuthClose}
          moduleId={sanctionsModuleId}
          moduleName="BEO Sanctions"
        />
      )}
    </>
  );
};

export default ModulesSection;
